import { Application, ColorMatrixFilter, Container, Graphics, type Sprite } from 'pixi.js';
import { AdvancedBloomFilter } from 'pixi-filters';
import type { World } from '../world/types.js';

export interface Layers {
  floor: Container;
  lights: Container;
  exitPad: Container;
  wallShadows: Container;
  walls: Container;
  sounds: Container;
  decoys: Container;
  vision: Container;
  cameras: Container;
  boxes: Container;
  bodies: Container;
  player: Container;
  guards: Container;
  particles: Container;
  alerts: Container;
}

export interface Stage {
  app: Application;
  layers: Layers;
  host: HTMLElement;
  /** Parent of every gameplay layer. Sits under app.stage so the camera
   * + shake offset still flows through. We mask THIS container (not
   * app.stage itself) when a level enables fog of war. */
  worldRoot: Container;
  /** Alpha mask drawn each frame — opaque inside the player's visible
   * region, empty elsewhere. Only consulted when worldRoot.mask is set. */
  fogMask: Graphics;
  /** Sibling of worldRoot — anything drawn here lives ABOVE the fog
   * mask (so it stays visible in the dark) and isn't translated by
   * the camera. Used for screen-edge HUD overlays like the exit
   * compass arrow on fog-of-war levels. */
  hudOverlay: Graphics;
  /** References to the bloom Sprite of each lamp, kept so the ticker can
   * modulate their alpha for a subtle flicker effect. Populated by
   * buildLights and reset every level rebuild. */
  lightFlickers: Sprite[];
  /** Visible window onto the world (px). May be smaller than the world,
   * in which case the camera scrolls; otherwise the world is centered. */
  viewportW: number;
  viewportH: number;
}

const LAYER_NAMES: Array<keyof Layers> = [
  'floor',
  'lights',
  'exitPad',
  'wallShadows',
  'walls',
  'sounds',
  'decoys',
  'vision',
  'cameras',
  'boxes',
  'bodies',
  'player',
  'guards',
  'particles',
  'alerts',
];

// Viewport caps. We never show more world than this on screen — past it,
// the camera scrolls. The mobile cap is smaller so tiles stay legible
// when CSS shrinks the canvas to a phone display.
const VIEWPORT_DESKTOP = { w: 768, h: 512 } as const; // 24 × 16 tiles
const VIEWPORT_MOBILE = { w: 480, h: 320 } as const; // 15 × 10 tiles

function isMobileViewport(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  // Touch + narrow window. The two media queries together avoid shrinking
  // the viewport on a regular desktop window that the user happened to
  // make narrow (which would just look bad).
  return matchMedia('(max-width: 700px), (pointer: coarse)').matches;
}

function clampViewport(world: World): { w: number; h: number } {
  const cap = isMobileViewport() ? VIEWPORT_MOBILE : VIEWPORT_DESKTOP;
  return {
    w: Math.min(world.WORLD_W, cap.w),
    h: Math.min(world.WORLD_H, cap.h),
  };
}

export async function createStage(world: World, host: HTMLElement): Promise<Stage> {
  const { w: viewportW, h: viewportH } = clampViewport(world);

  const app = new Application();
  await app.init({
    width: viewportW,
    height: viewportH,
    background: '#0a0b12',
    antialias: true,
    autoDensity: true,
    resolution: window.devicePixelRatio || 1,
  });

  host.appendChild(app.canvas as unknown as HTMLCanvasElement);

  // World root — every gameplay layer hangs off this so we can mask
  // the whole game with one assignment when a level enables fog.
  const worldRoot = new Container();
  worldRoot.label = 'worldRoot';
  app.stage.addChild(worldRoot);

  // Real HDR-style bloom on the entire scene. The threshold extracts
  // bright pixels (lamps, additive layers, exit pad), blurs them, and
  // adds them back — gives lamps a real glow halo, sparks a comet
  // streak, the exit pad a soft pulsing aura.
  //
  // Tuning notes: AdvancedBloomFilter wraps a KawaseBlur. With low
  // quality and large pixelSize the result shows visible cross/diamond
  // pixel artifacts around small bright spots (lamps especially). We
  // counter that with `pixelSize` < 1 (tighter sample spacing → less
  // blocky) and bumped `quality` (more passes → closer to a real
  // Gaussian). `threshold` raised so only the brightest core pixels
  // bloom, keeping the effect focused.
  worldRoot.filters = [
    new AdvancedBloomFilter({
      threshold: 0.7,
      bloomScale: 0.85,
      brightness: 1,
      blur: 8,
      quality: 8,
      pixelSize: { x: 0.5, y: 0.5 },
    }),
  ];

  // Cinematic colour grade on the whole rendered output. Slightly
  // pushes saturation + contrast and lifts a hint of warmth, mimics
  // the look of a colour-graded film. Done on the GPU instead of CSS
  // so it composites correctly with the bloom above.
  const grade = new ColorMatrixFilter();
  grade.saturate(0.18, true);
  grade.contrast(0.1, true);
  grade.brightness(1.03, true);
  app.stage.filters = [grade];

  // Fog mask graphics — must live in the display tree so Pixi can build
  // its geometry. Pixi v8 auto-flags the object as `isMask=true` once
  // it's assigned via `worldRoot.mask`, which excludes it from normal
  // rendering (so we don't need `renderable=false` — and setting that
  // flag actually breaks mask geometry upload, leaving the mask empty
  // and the whole container hidden).
  const fogMask = new Graphics();
  fogMask.label = 'fogMask';
  worldRoot.addChild(fogMask);

  const layers = {} as Layers;
  for (const name of LAYER_NAMES) {
    const c = new Container();
    c.label = name;
    worldRoot.addChild(c);
    layers[name] = c;
  }

  // HUD overlay — sibling of worldRoot so it stays visible above the
  // fog mask and unaffected by the camera+shake transform written to
  // app.stage.position. Used for screen-edge cues like the exit
  // compass on fog-of-war levels.
  const hudOverlay = new Graphics();
  hudOverlay.label = 'hudOverlay';
  app.stage.addChild(hudOverlay);

  const stage: Stage = {
    app,
    layers,
    host,
    worldRoot,
    fogMask,
    hudOverlay,
    lightFlickers: [],
    viewportW,
    viewportH,
  };

  fitCanvas(stage);
  addEventListener('resize', () => fitCanvas(stage));

  return stage;
}

/** Adjust the visible window when the level changes. Pixi resizes the
 * underlying renderer, then we re-fit the canvas to the host. Does
 * nothing if the new viewport matches the current one. */
export function resizeViewport(stage: Stage, world: World): void {
  const { w, h } = clampViewport(world);
  if (w === stage.viewportW && h === stage.viewportH) return;
  stage.viewportW = w;
  stage.viewportH = h;
  stage.app.renderer.resize(w, h);
  fitCanvas(stage);
}

function fitCanvas(stage: Stage): void {
  const canvas = stage.app.canvas as unknown as HTMLCanvasElement;
  const pad = 16;
  const aw = innerWidth - pad * 2;
  const ah = innerHeight - pad * 2;
  const sx = aw / stage.viewportW;
  const sy = ah / stage.viewportH;
  const s = Math.min(sx, sy);
  canvas.style.width = `${(stage.viewportW * s) | 0}px`;
  canvas.style.height = `${(stage.viewportH * s) | 0}px`;
  canvas.style.display = 'block';
}
